/**
 * The add-players drawer's filtered-view candidate universe (SE-7090).
 *
 * Assembles the union of the drawer's own pools — Watchlists + My Roster +
 * the loaded Suggested candidates — as the set the committed filters are
 * evaluated over. This replaces the global `filterPlayers` read the filtered
 * view used to run, which bypassed the drawer's pools entirely and produced
 * false 0-result filters (Pietra's Roles + League repro).
 *
 * ## Pools are walked to EXHAUSTION
 *
 * A filter verdict over a partially-loaded pool is a false negative waiting
 * to happen, so both pool reads eagerly walk their remaining pages until
 * `hasNextPage` is false — the Squad Planning SE-5158 pattern, including its
 * in-flight guard that resumes after a transient failure instead of latching
 * a permanent "done". The relayStylePagination policies key these root
 * fields without first/after, so the walked pages merge into the tab bodies'
 * cache entries rather than fragmenting them.
 * @module features/player-kanban/hooks/useAddPlayersFilteredPool
 */

import { useEffect, useMemo, useRef } from "react";

import {
  useKanbanFilteredMyOrganizationsPlayersQuery,
  useKanbanFilteredWatchlistPlayersQuery,
  type SuggestionReasonCategory,
} from "@/generated/graphql";
import { useTeamSelection } from "@/providers/TeamSelectionProvider";

import type { SuggestedCandidate } from "./useSuggestedPlayers";
import type { ServerGpvRange } from "../utils/playerFilter";

/**
 * Page size for the exhaustive pool walk. Larger than the tabs' 40 on
 * purpose — the walk wants the whole pool in as few round-trips as the
 * backend's page cap allows, and the cache entries merge either way.
 */
const POOL_PAGE_SIZE = 200;

/**
 * The structural player shape a union row carries — the filterable fields the
 * predicates read plus the display fields the result card renders. Both the
 * `KanbanFilterablePlayerData` pool rows and the enriched suggested-candidate
 * players satisfy it, so the union needs no casts.
 */
export interface FilteredPoolPlayer {
  readonly id: string;
  readonly name?: string | null;
  readonly knownName?: string | null;
  readonly teamName?: string | null;
  readonly team?: {
    readonly id: string;
    readonly name?: string | null;
  } | null;
  readonly league?: { readonly id: string } | null;
  readonly watchlists?: readonly { readonly id: string }[] | null;
  readonly positions?:
    | readonly ({
        readonly id: string;
        readonly abbreviation?: string | null;
      } | null)[]
    | null;
  readonly bioData?: {
    readonly age?: number | null;
    readonly gpr?: number | null;
    readonly nationality?: string | null;
    readonly contractExpires?: string | null;
    readonly roleArchetype?: string | null;
    readonly secondaryRoleArchetype?: string | null;
    readonly geminiPlayerValuation?: number | null;
    readonly fitScore?: number | null;
    readonly teamStyleFit?: number | null;
    readonly carryingTransGlobalCategoricalScore?: number | null;
    readonly dribblingTransGlobalCategoricalScore?: number | null;
    readonly passingTransGlobalCategoricalScore?: number | null;
    readonly shotLocationTransGlobalCategoricalScore?: number | null;
    readonly gkPassingTransCategoricalScore?: number | null;
    readonly gkShotStoppingTransCategoricalScore?: number | null;
    readonly gkPositioningTransCategoricalScore?: number | null;
    /** REPORT STATS source (SE-8608) — selected by every pool, Suggested included. */
    readonly customStats?: unknown;
  } | null;
}

/** One row of the filtered view's candidate universe. */
export interface FilteredPoolRow {
  /** The player, carrying every filterable field. */
  readonly player: FilteredPoolPlayer;
  /**
   * The suggestion's reason categories, when this player is also a loaded
   * Suggested candidate — the result card renders them, same as the
   * Suggested tab.
   */
  readonly reasons?: readonly SuggestionReasonCategory[];
}

/** Inputs for {@link useAddPlayersFilteredPool}. */
interface UseAddPlayersFilteredPoolOptions {
  /** Whether the filtered view needs the union — false skips every read. */
  readonly isActive: boolean;
  /** Every org watchlist id — the Watchlists pool, same set as the tab. */
  readonly watchlistIds: readonly string[];
  /** Active board cost-filter GPV bounds, mirrored from the tab (SE-6496). */
  readonly serverGpvRange?: ServerGpvRange | null;
  /** The loaded Suggested candidates, already in bestRank order. */
  readonly candidates: readonly SuggestedCandidate[];
}

/** Result of {@link useAddPlayersFilteredPool}. */
interface UseAddPlayersFilteredPoolResult {
  /**
   * The deduplicated union, Suggested candidates first (bestRank order, as
   * received), then the remaining pool players sorted by name. Rows keep
   * arriving while the page walk runs.
   */
  readonly rows: readonly FilteredPoolRow[];
  /** Whether either pool is still on its FIRST load (nothing to show yet). */
  readonly isLoading: boolean;
  /** Whether either pool read failed. */
  readonly isError: boolean;
}

/**
 * Eagerly walk a paginated pool to exhaustion (SE-5158 pattern).
 * @param pageInfo - The connection's current page info.
 * @param fetchMore - The query's fetchMore, called with `after` only — the
 * remaining variables are inherited from the query.
 * @param enabled - Whether walking is wanted at all.
 */
const usePoolPageWalk = (
  pageInfo: { hasNextPage: boolean; endCursor?: string | null } | undefined,
  fetchMore: (options: { variables: { after: string } }) => Promise<unknown>,
  enabled: boolean
): void => {
  const fetchMoreRef = useRef(fetchMore);
  const isFetchingRef = useRef(false);
  const hasNextPage = pageInfo?.hasNextPage === true;
  const endCursor = pageInfo?.endCursor ?? null;

  useEffect(() => {
    fetchMoreRef.current = fetchMore;
  }, [fetchMore]);

  useEffect(() => {
    if (!enabled || !hasNextPage || !endCursor || isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    void fetchMoreRef
      .current({ variables: { after: endCursor } })
      .finally(() => {
        // Cleared (not latched) so a transient failure can resume; on success
        // the cache advances pageInfo and this effect re-fires for the next
        // page until hasNextPage is false.
        isFetchingRef.current = false;
      });
  }, [enabled, hasNextPage, endCursor]);
};

/**
 * Read the filtered view's candidate universe.
 * @param options - See {@link UseAddPlayersFilteredPoolOptions}.
 * @param options.isActive - Whether the filtered view needs the union.
 * @param options.watchlistIds - Every org watchlist id.
 * @param options.serverGpvRange - Active board cost-filter GPV bounds.
 * @param options.candidates - The loaded Suggested candidates.
 * @returns The union rows and their load state.
 */
const useAddPlayersFilteredPool = ({
  isActive,
  watchlistIds,
  serverGpvRange = null,
  candidates,
}: UseAddPlayersFilteredPoolOptions): UseAddPlayersFilteredPoolResult => {
  const { isLoaded: isTeamSelectionLoaded } = useTeamSelection();

  const watchlistQuery = useKanbanFilteredWatchlistPlayersQuery({
    variables: {
      watchlistIds: [...watchlistIds],
      first: POOL_PAGE_SIZE,
      ...(serverGpvRange
        ? {
            minGeminiPlayerValuation: serverGpvRange.min,
            maxGeminiPlayerValuation: serverGpvRange.max,
          }
        : {}),
    },
    skip: !isActive || watchlistIds.length === 0,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const rosterQuery = useKanbanFilteredMyOrganizationsPlayersQuery({
    variables: { first: POOL_PAGE_SIZE },
    skip: !isActive || !isTeamSelectionLoaded,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const watchlistEdges = watchlistQuery.data?.listWatchlistPlayers?.edges;
  const rosterEdges = rosterQuery.data?.listMyOrganizationsPlayers?.edges;

  const rows = useMemo<readonly FilteredPoolRow[]>(() => {
    if (!isActive) return [];

    /* Suggested first, deduplicated positionally so bestRank order holds. */
    const suggested: readonly FilteredPoolRow[] = candidates
      .filter(
        (candidate, index) =>
          candidates.findIndex(
            other => other.player.id === candidate.player.id
          ) === index
      )
      .map(candidate => ({
        player: candidate.player,
        reasons: candidate.reasons,
      }));
    const suggestedIds = suggested.map(row => row.player.id);

    const poolNodes = [
      ...(watchlistEdges ?? []).map(edge => edge.node),
      ...(rosterEdges ?? []).map(edge => edge.node),
    ];
    const poolPlayers = poolNodes
      .filter(
        (player, index) =>
          !suggestedIds.includes(player.id) &&
          poolNodes.findIndex(other => other.id === player.id) === index
      )
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
      .map(player => ({ player }));

    return [...suggested, ...poolPlayers];
  }, [candidates, isActive, rosterEdges, watchlistEdges]);

  const isLoading =
    isActive &&
    ((watchlistQuery.loading && watchlistEdges === undefined) ||
      (rosterQuery.loading && rosterEdges === undefined));

  const isError =
    isActive &&
    (watchlistQuery.error !== undefined || rosterQuery.error !== undefined);

  usePoolPageWalk(
    watchlistQuery.data?.listWatchlistPlayers?.pageInfo,
    watchlistQuery.fetchMore,
    isActive && watchlistIds.length > 0
  );
  usePoolPageWalk(
    rosterQuery.data?.listMyOrganizationsPlayers?.pageInfo,
    rosterQuery.fetchMore,
    isActive && isTeamSelectionLoaded
  );

  return { rows, isLoading, isError };
};

export default useAddPlayersFilteredPool;
